/**
 * AssistantFrame — the imported assistant design's desktop composition
 * (spec §4.2/§4.3, assistant-surface plan Task 8).
 *
 * Two gates, both of which must pass before a single design pixel renders:
 *
 *   1. `useDesignFrame()` — the viewport clears 1200px. Below that the design
 *      draws no layout at all, so this component returns its two children as
 *      the plain siblings `ChatRoute` has always rendered. Any pixel movement
 *      below 1200px is a defect in this work (plan, Global Constraints).
 *   2. `useDesignWorld()` — the resolved theme is light. The imported world
 *      ships ONE palette and no dark ramp (Ruling R17, Task 6/7). This frame
 *      paints `--gd-ground`, a cream; the transcript inside it still carries
 *      `--g-*` ink, which in dark theme is nearly white. Rendering the frame
 *      in dark theme would be white text on cream — so dark keeps today's
 *      composition, exactly as the desktop header does.
 *
 * The frame itself is deliberately thin: a flex row, the design's ground, and
 * the shyrdak lattice (`assistant.css`). It does NOT wrap either child in a
 * box of its own — the rail owns its width and its dark treatment, the
 * conversation owns its column. A wrapper here would silently double the
 * rail's 256px and is the kind of structural edit the mobile regression gate
 * exists to catch.
 *
 * Both gates are read through `useAssistantDesign`, which the rail and
 * `ChatRoute` also call — one answer, or the surface renders half-designed.
 */
import type { ReactElement, ReactNode } from "react";

import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import "@/features/chat/assistant.css";

export interface AssistantFrameProps {
  /** The thread rail. Renders inside the frame, or as a plain sibling below it. */
  sidebar: ReactNode;
  /** The conversation pane. */
  children: ReactNode;
}

export function AssistantFrame({
  sidebar,
  children,
}: AssistantFrameProps): ReactElement {
  const design = useAssistantDesign();

  if (!design) {
    return (
      <>
        {sidebar}
        {children}
      </>
    );
  }

  return (
    <div
      className="gd-assistant flex h-full w-full min-w-0"
      data-testid="assistant-frame"
    >
      {sidebar}
      {children}
    </div>
  );
}

export default AssistantFrame;
